import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { callWithAuth, checkoutSession } from "@/api/Api";
import { STRIPE_PUBLISHABLE_KEY } from "@/api/base";
import BackButton from "@/components/Buttons/BackButton";
import ParrotText from "@/components/ParrotText";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import ParrotSpinner from "@/components/ParrotSpinner";
import { useToast } from "@chakra-ui/react";
// Load Stripe outside of the component render
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const StripeCheckout: React.FC = () => {
  const nav = useNavigate();
  const { "*": backUrl } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const sku = queryParams.get("sku");

  const sessionId = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const fetchClientSecret = async () => {
    setIsLoading(true);
    try {
      const response = await callWithAuth(checkoutSession, {
        sku: sku ?? "monthly_sub",
      });
      sessionId.current = response.id as string;
      setIsLoading(false);
      return response.clientSecret;
    } catch (error) {
      console.error("Error fetching client secret:", error);
      toast({
        title:
          "Error setting up the checkout, please contact support@fluentwithparrot.com",
        status: "error",
        duration: 10000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
    return "";
  };

  const handleComplete = () => {
    if (backUrl) {
      nav(`/complete/signup?session_id=${sessionId.current}&sku=${sku}`);
    } else {
      nav(`/complete/${backUrl}?session_id=${sessionId.current}&sku=${sku}`);
    }
  };

  const options = { fetchClientSecret };

  return (
    <div className="w-full h-full appPadding">
      <BackButton
        className="!absolute top-0 left-0 m-6"
        onBack={() => {
          nav("/premium");
        }}
      />
      <ParrotText />
      <p className="pt-6 pb-3 text-xl text-center font-bold">
        Parrot+ Subscription
      </p>
      {isLoading && (
        <div className="flex justify-center items-center h-1/2">
          <ParrotSpinner />
        </div>
      )}
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          ...options,
          onComplete: handleComplete,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default StripeCheckout;
